import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import {
  Plus,
  Edit,
  Loader2,
  Warehouse as WarehouseIcon,
  ArrowLeftRight,
  AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const getErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (typeof detail?.message === 'string' && detail.message.trim()) return detail.message;
  return fallback;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function WarehousePage() {
  const [warehouses, setWarehouses] = useState([]);
  const [variants, setVariants] = useState([]);
  const [rawItems, setRawItems] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [selectedRawWarehouseId, setSelectedRawWarehouseId] = useState('');
  const [stock, setStock] = useState([]);
  const [rawLots, setRawLots] = useState([]);
  const [movements, setMovements] = useState([]);
  const [rawMovements, setRawMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(false);
  const [movementLoading, setMovementLoading] = useState(false);
  const [rawStockLoading, setRawStockLoading] = useState(false);
  const [rawMovementLoading, setRawMovementLoading] = useState(false);
  const [packingRequests, setPackingRequests] = useState([]);
  const [packingLoading, setPackingLoading] = useState(false);
  const [packingStatus, setPackingStatus] = useState('pending');
  const [packingSearch, setPackingSearch] = useState('');
  const [packingDateFrom, setPackingDateFrom] = useState('');
  const [packingDateTo, setPackingDateTo] = useState('');
  const [activeTab, setActiveTab] = useState('finished');

  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showEditAdjustModal, setShowEditAdjustModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showRawItemModal, setShowRawItemModal] = useState(false);
  const [showRawReceiveModal, setShowRawReceiveModal] = useState(false);
  const [showRawTransferModal, setShowRawTransferModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPackingCompleteModal, setShowPackingCompleteModal] = useState(false);
  const [selectedPackingRequest, setSelectedPackingRequest] = useState(null);
  const [packingRemarks, setPackingRemarks] = useState('');

  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedRawItem, setSelectedRawItem] = useState(null);
  const [warehouseForm, setWarehouseForm] = useState({
    name: '',
    code: '',
    address: '',
    tag: 'SALES',
    is_active: true,
    is_default_sales: false,
    warehouse_type: 'SALES',
    is_default_return: false
  });
  const [rawItemForm, setRawItemForm] = useState({
    name: '',
    is_active: true
  });

  const [adjustForm, setAdjustForm] = useState({
    variant_id: '',
    qty: '',
    note: '',
    batch_no: ''
  });

  const [editAdjustForm, setEditAdjustForm] = useState({
    id: '',
    qty: '',
    note: '',
    batch_no: ''
  });

  const [transferForm, setTransferForm] = useState({
    from_warehouse_id: '',
    to_warehouse_id: '',
    variant_id: '',
    qty: '',
    note: '',
    batch_no: ''
  });

  const [rawReceiveForm, setRawReceiveForm] = useState({
    warehouse_id: '',
    raw_item_id: '',
    bags_count: '',
    kg_per_bag: '',
    received_at: '',
    note: ''
  });

  const [rawTransferForm, setRawTransferForm] = useState({
    from_warehouse_id: '',
    to_warehouse_id: '',
    raw_item_id: '',
    kg_required: '',
    note: ''
  });

  const [rawSuggestions, setRawSuggestions] = useState([]);
  const [rawSuggestionTotals, setRawSuggestionTotals] = useState({ total_bags: 0, total_kg: 0 });
  const [rawStockRefPreview, setRawStockRefPreview] = useState('');

  const [movementType, setMovementType] = useState('all');
  const [movementVariant, setMovementVariant] = useState('all');
  const [movementDateFrom, setMovementDateFrom] = useState('');
  const [movementDateTo, setMovementDateTo] = useState('');

  const [rawMovementType, setRawMovementType] = useState('all');
  const [rawMovementItem, setRawMovementItem] = useState('all');
  const [rawMovementDateFrom, setRawMovementDateFrom] = useState('');
  const [rawMovementDateTo, setRawMovementDateTo] = useState('');

  useEffect(() => {
    fetchInitial();
  }, []);

  useEffect(() => {
    if (!selectedWarehouseId) return;
    fetchStock(selectedWarehouseId);
  }, [selectedWarehouseId]);

  useEffect(() => {
    if (!selectedRawWarehouseId) return;
    fetchRawLots(selectedRawWarehouseId);
  }, [selectedRawWarehouseId]);

  useEffect(() => {
    if (!selectedWarehouseId) return;
    fetchMovements();
  }, [selectedWarehouseId, movementType, movementVariant, movementDateFrom, movementDateTo]);

  useEffect(() => {
    if (!selectedRawWarehouseId) return;
    fetchRawMovements();
  }, [selectedRawWarehouseId, rawMovementType, rawMovementItem, rawMovementDateFrom, rawMovementDateTo]);

  useEffect(() => {
    if (activeTab !== 'packing') return;
    fetchPackingRequests();
  }, [activeTab, packingStatus, packingSearch, packingDateFrom, packingDateTo]);

  useEffect(() => {
    const shouldSuggest =
      rawTransferForm.from_warehouse_id &&
      rawTransferForm.raw_item_id &&
      rawTransferForm.kg_required;

    if (!shouldSuggest) {
      setRawSuggestions([]);
      setRawSuggestionTotals({ total_bags: 0, total_kg: 0 });
      return;
    }

    const fetchSuggestions = async () => {
      try {
        const response = await adminApi.suggestRawStock({
          warehouse_id: rawTransferForm.from_warehouse_id,
          raw_item_id: rawTransferForm.raw_item_id,
          kg_required: parseFloat(rawTransferForm.kg_required)
        });
        setRawSuggestions(response.data?.suggestions || []);
        setRawSuggestionTotals({
          total_bags: response.data?.total_bags || 0,
          total_kg: response.data?.total_kg || 0
        });
      } catch (error) {
        setRawSuggestions([]);
        setRawSuggestionTotals({ total_bags: 0, total_kg: 0 });
      }
    };

    fetchSuggestions();
  }, [rawTransferForm.from_warehouse_id, rawTransferForm.raw_item_id, rawTransferForm.kg_required]);

  useEffect(() => {
    if (!showRawReceiveModal) return;
    const fetchRef = async () => {
      try {
        const response = await adminApi.getNextRawStockRef();
        setRawStockRefPreview(response.data?.stock_ref_no || '');
      } catch (error) {
        setRawStockRefPreview('');
      }
    };
    fetchRef();
  }, [showRawReceiveModal]);

  const fetchInitial = async () => {
    setLoading(true);
    try {
      const [warehouseRes, variantsRes, rawItemsRes] = await Promise.all([
        adminApi.getWarehouses(),
        adminApi.getProductVariants(),
        adminApi.getRawItems()
      ]);
      const warehouseList = warehouseRes.data || [];
      setWarehouses(warehouseList);
      setVariants(variantsRes.data || []);
      setRawItems(rawItemsRes.data || []);

      if (warehouseList.length > 0 && !selectedWarehouseId) {
        const defaultWarehouse = warehouseList.find((w) => w.is_default_sales);
        setSelectedWarehouseId(defaultWarehouse?.id || warehouseList[0].id);
      }

      if (warehouseList.length > 0 && !selectedRawWarehouseId) {
        const defaultWarehouse = warehouseList.find((w) => w.is_default_sales);
        setSelectedRawWarehouseId(defaultWarehouse?.id || warehouseList[0].id);
      }
    } catch (error) {
      toast.error('Failed to load warehouse data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStock = async (warehouseId) => {
    setStockLoading(true);
    try {
      const response = await adminApi.getWarehouseStock(warehouseId);
      setStock(response.data || []);
    } catch (error) {
      toast.error('Failed to load warehouse stock');
    } finally {
      setStockLoading(false);
    }
  };

  const fetchMovements = async () => {
    setMovementLoading(true);
    try {
      const params = {
        warehouse_id: selectedWarehouseId,
      };
      if (movementType !== 'all') params.movement_type = movementType;
      if (movementVariant !== 'all') params.variant_id = movementVariant;
      if (movementDateFrom) params.date_from = movementDateFrom;
      if (movementDateTo) params.date_to = movementDateTo;

      const response = await adminApi.getStockMovements(params);
      setMovements(response.data || []);
    } catch (error) {
      toast.error('Failed to load stock movements');
    } finally {
      setMovementLoading(false);
    }
  };

  const fetchRawLots = async (warehouseId) => {
    setRawStockLoading(true);
    try {
      const response = await adminApi.getRawStockLots({ warehouse_id: warehouseId });
      setRawLots(response.data || []);
    } catch (error) {
      toast.error('Failed to load raw stock');
    } finally {
      setRawStockLoading(false);
    }
  };

  const fetchRawMovements = async () => {
    setRawMovementLoading(true);
    try {
      const params = {
        warehouse_id: selectedRawWarehouseId,
      };
      if (rawMovementType !== 'all') params.movement_type = rawMovementType;
      if (rawMovementItem !== 'all') params.raw_item_id = rawMovementItem;
      if (rawMovementDateFrom) params.date_from = rawMovementDateFrom;
      if (rawMovementDateTo) params.date_to = rawMovementDateTo;

      const response = await adminApi.getRawStockMovements(params);
      setRawMovements(response.data || []);
    } catch (error) {
      toast.error('Failed to load raw stock movements');
    } finally {
      setRawMovementLoading(false);
    }
  };

  const fetchPackingRequests = async () => {
    setPackingLoading(true);
    try {
      const params = {};
      if (packingStatus && packingStatus !== 'all') params.status = packingStatus;
      if (packingSearch) params.search = packingSearch;
      if (packingDateFrom) params.date_from = packingDateFrom;
      if (packingDateTo) params.date_to = packingDateTo;
      const response = await adminApi.getPackingRequests(params);
      setPackingRequests(response.data?.items || []);
    } catch (error) {
      toast.error('Failed to load packing requests');
    } finally {
      setPackingLoading(false);
    }
  };

  const handleOpenPackingComplete = (request) => {
    setSelectedPackingRequest(request);
    setPackingRemarks('');
    setShowPackingCompleteModal(true);
  };

  const handleCompletePackingRequest = async () => {
    if (!selectedPackingRequest) return;
    setSaving(true);
    try {
      await adminApi.completePackingRequest(selectedPackingRequest.id, { remarks: packingRemarks });
      toast.success('Packing request completed');
      setShowPackingCompleteModal(false);
      setSelectedPackingRequest(null);
      setPackingRemarks('');
      fetchPackingRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete packing request');
    } finally {
      setSaving(false);
    }
  };

  const resetWarehouseForm = () => {
    setWarehouseForm({
      name: '',
      code: '',
      address: '',
      tag: 'SALES',
      is_active: true,
      is_default_sales: false,
      warehouse_type: 'SALES',
      is_default_return: false
    });
    setSelectedWarehouse(null);
  };

  const resetAdjustForm = () => {
    setAdjustForm({
      variant_id: '',
      qty: '',
      note: '',
      batch_no: ''
    });
  };

  const resetTransferForm = () => {
    setTransferForm({
      from_warehouse_id: selectedWarehouseId || '',
      to_warehouse_id: '',
      variant_id: '',
      qty: '',
      note: '',
      batch_no: ''
    });
  };

  const resetRawItemForm = () => {
    setRawItemForm({
      name: '',
      is_active: true
    });
    setSelectedRawItem(null);
  };

  const resetRawReceiveForm = () => {
    setRawReceiveForm({
      warehouse_id: selectedRawWarehouseId || '',
      raw_item_id: '',
      bags_count: '',
      kg_per_bag: '',
      received_at: '',
      note: ''
    });
    setRawStockRefPreview('');
  };

  const resetRawTransferForm = () => {
    setRawTransferForm({
      from_warehouse_id: selectedRawWarehouseId || '',
      to_warehouse_id: '',
      raw_item_id: '',
      kg_required: '',
      note: ''
    });
    setRawSuggestions([]);
    setRawSuggestionTotals({ total_bags: 0, total_kg: 0 });
  };

  const handleSaveWarehouse = async () => {
    if (!warehouseForm.name.trim()) {
      toast.error('Please enter warehouse name');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...warehouseForm,
        code: warehouseForm.code || undefined,
        address: warehouseForm.address || ''
      };

      if (selectedWarehouse) {
        await adminApi.updateWarehouse(selectedWarehouse.id, payload);
        toast.success('Warehouse updated');
      } else {
        await adminApi.createWarehouse(payload);
        toast.success('Warehouse created');
      }
      setShowWarehouseModal(false);
      resetWarehouseForm();
      fetchInitial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save warehouse');
    } finally {
      setSaving(false);
    }
  };

  const handleEditWarehouse = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setWarehouseForm({
      name: warehouse.name || '',
      code: warehouse.code || '',
      address: warehouse.address || '',
      tag: warehouse.tag || (warehouse.warehouse_type === 'RETURN' ? 'RETURN' : 'SALES'),
      is_active: warehouse.is_active,
      is_default_sales: warehouse.is_default_sales,
      warehouse_type: warehouse.warehouse_type || 'SALES',
      is_default_return: warehouse.is_default_return
    });
    setShowWarehouseModal(true);
  };

  const handleEditRawItem = (item) => {
    setSelectedRawItem(item);
    setRawItemForm({
      name: item.name || '',
      is_active: item.is_active
    });
    setShowRawItemModal(true);
  };

  const handleSaveRawItem = async () => {
    if (!rawItemForm.name.trim()) {
      toast.error('Please enter raw item name');
      return;
    }
    setSaving(true);
    try {
      if (selectedRawItem) {
        await adminApi.updateRawItem(selectedRawItem.id, rawItemForm);
        toast.success('Raw item updated');
      } else {
        await adminApi.createRawItem(rawItemForm);
        toast.success('Raw item created');
      }
      setShowRawItemModal(false);
      resetRawItemForm();
      const res = await adminApi.getRawItems();
      setRawItems(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save raw item');
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (warehouseId) => {
    try {
      await adminApi.updateWarehouse(warehouseId, { is_default_sales: true });
      toast.success('Default sales warehouse updated');
      fetchInitial();
    } catch (error) {
      toast.error('Failed to update default warehouse');
    }
  };

  const handleSetDefaultReturn = async (warehouseId) => {
    try {
      await adminApi.updateWarehouse(warehouseId, { is_default_return: true });
      toast.success('Default return warehouse updated');
      fetchInitial();
    } catch (error) {
      toast.error('Failed to update default return warehouse');
    }
  };

  const handleAdjustStock = async () => {
    if (!selectedWarehouseId) {
      toast.error('Select a warehouse first');
      return;
    }
    if (!adjustForm.variant_id) {
      toast.error('Select a product variant');
      return;
    }
    if (!adjustForm.qty) {
      toast.error('Enter quantity');
      return;
    }

    setSaving(true);
    try {
      await adminApi.adjustWarehouseStock(selectedWarehouseId, {
        items: [
          {
            variant_id: adjustForm.variant_id,
            qty: parseFloat(adjustForm.qty),
            note: adjustForm.note || undefined,
            batch_no: adjustForm.batch_no || undefined
          }
        ]
      });
      toast.success('Stock adjusted');
      setShowAdjustModal(false);
      resetAdjustForm();
      fetchStock(selectedWarehouseId);
      fetchMovements();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to adjust stock'));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEditAdjustment = (movement) => {
    setEditAdjustForm({
      id: movement.id,
      qty: String(movement.qty ?? ''),
      note: movement.note || '',
      batch_no: movement.batch_no || ''
    });
    setShowEditAdjustModal(true);
  };

  const handleUpdateAdjustment = async () => {
    if (!editAdjustForm.id) return;
    if (!editAdjustForm.qty) {
      toast.error('Quantity is required');
      return;
    }

    setSaving(true);
    try {
      await adminApi.updateStockMovement(editAdjustForm.id, {
        qty: parseFloat(editAdjustForm.qty),
        note: editAdjustForm.note || undefined,
        batch_no: editAdjustForm.batch_no || undefined
      });
      toast.success('Stock adjustment updated');
      setShowEditAdjustModal(false);
      fetchStock(selectedWarehouseId);
      fetchMovements();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update adjustment'));
    } finally {
      setSaving(false);
    }
  };

  const handleTransferStock = async () => {
    if (!transferForm.from_warehouse_id || !transferForm.to_warehouse_id) {
      toast.error('Select source and destination');
      return;
    }
    if (transferForm.from_warehouse_id === transferForm.to_warehouse_id) {
      toast.error('Source and destination must be different');
      return;
    }
    if (!transferForm.variant_id) {
      toast.error('Select a product variant');
      return;
    }
    if (!transferForm.qty) {
      toast.error('Enter quantity');
      return;
    }

    setSaving(true);
    try {
      await adminApi.transferStock({
        from_warehouse_id: transferForm.from_warehouse_id,
        to_warehouse_id: transferForm.to_warehouse_id,
        items: [
          {
            variant_id: transferForm.variant_id,
            qty: parseFloat(transferForm.qty),
            note: transferForm.note || undefined,
            batch_no: transferForm.batch_no || undefined
          }
        ],
        note: transferForm.note || undefined
      });
      toast.success('Stock transferred');
      setShowTransferModal(false);
      resetTransferForm();
      fetchStock(selectedWarehouseId);
      fetchMovements();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to transfer stock'));
    } finally {
      setSaving(false);
    }
  };

  const handleReceiveRawStock = async () => {
    if (!rawReceiveForm.warehouse_id || !rawReceiveForm.raw_item_id) {
      toast.error('Select warehouse and raw item');
      return;
    }
    if (!rawReceiveForm.bags_count || !rawReceiveForm.kg_per_bag) {
      toast.error('Enter bags and kg');
      return;
    }

    setSaving(true);
    try {
      await adminApi.receiveRawStock({
        warehouse_id: rawReceiveForm.warehouse_id,
        raw_item_id: rawReceiveForm.raw_item_id,
        bags_count: parseInt(rawReceiveForm.bags_count, 10),
        kg_per_bag: parseFloat(rawReceiveForm.kg_per_bag),
        received_at: rawReceiveForm.received_at || undefined,
        note: rawReceiveForm.note || undefined
      });
      toast.success('Raw stock received');
      setShowRawReceiveModal(false);
      resetRawReceiveForm();
      fetchRawLots(selectedRawWarehouseId);
      fetchRawMovements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive raw stock');
    } finally {
      setSaving(false);
    }
  };

  const handleTransferRawStock = async () => {
    if (!rawTransferForm.from_warehouse_id || !rawTransferForm.to_warehouse_id) {
      toast.error('Select source and destination');
      return;
    }
    if (rawTransferForm.from_warehouse_id === rawTransferForm.to_warehouse_id) {
      toast.error('Source and destination must be different');
      return;
    }
    if (!rawTransferForm.raw_item_id) {
      toast.error('Select raw item');
      return;
    }
    if (!rawTransferForm.kg_required || parseFloat(rawTransferForm.kg_required) <= 0) {
      toast.error('Enter required kg');
      return;
    }

    setSaving(true);
    try {
      await adminApi.transferRawStock({
        from_warehouse_id: rawTransferForm.from_warehouse_id,
        to_warehouse_id: rawTransferForm.to_warehouse_id,
        raw_item_id: rawTransferForm.raw_item_id,
        kg_required: parseFloat(rawTransferForm.kg_required),
        note: rawTransferForm.note || undefined
      });
      toast.success('Raw stock transferred');
      setShowRawTransferModal(false);
      resetRawTransferForm();
      fetchRawLots(selectedRawWarehouseId);
      fetchRawMovements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer raw stock');
    } finally {
      setSaving(false);
    }
  };

  const warehouseOptions = useMemo(() => warehouses.filter((w) => w.is_active), [warehouses]);
  const variantOptions = useMemo(() => variants.filter((v) => v.is_active), [variants]);
  const rawItemOptions = useMemo(() => rawItems.filter((r) => r.is_active), [rawItems]);

  return (
    <div className="space-y-6" data-testid="warehouse-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Warehouse</h1>
        <p className="text-[#A8A8A8] mt-1">Manage warehouses and inventory movements</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white border border-[#E5E5E5]">
          <TabsTrigger value="finished">Finished Goods</TabsTrigger>
          <TabsTrigger value="raw">Raw Materials</TabsTrigger>
          <TabsTrigger value="packing">Packing Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="finished" className="space-y-6">
          <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#2D2420]">Warehouses</h2>
              <p className="text-sm text-[#A8A8A8]">Set your default sales warehouse</p>
            </div>
            <Button onClick={() => { resetWarehouseForm(); setShowWarehouseModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Warehouse
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : warehouses.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-[#A8A8A8]">
              <WarehouseIcon className="w-6 h-6 mr-2" />
              No warehouses yet
            </div>
          ) : (
            <div className="overflow-x-auto mt-4">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Code</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Tag</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Default</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {warehouses.map((warehouse) => (
                    <tr key={warehouse.id} className="hover:bg-[#FAFAF9]/60">
                      <td className="py-3 px-4 text-[#2D2420] font-medium">{warehouse.name}</td>
                      <td className="py-3 px-4 text-[#A8A8A8]">{warehouse.code}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">
                        {warehouse.warehouse_type === 'RETURN' ? 'Return' : 'Sales'}
                      </td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{warehouse.tag || '-'}</td>
                      <td className="py-3 px-4">
                        <Badge className={cn("text-xs", warehouse.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800")}>
                          {warehouse.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        {warehouse.is_default_sales ? (
                          <Badge className="text-xs bg-[#C97A5E]/10 text-[#C97A5E]">Default Sales</Badge>
                        ) : warehouse.is_default_return ? (
                          <Badge className="text-xs bg-[#C97A5E]/10 text-[#C97A5E]">Default Return</Badge>
                        ) : (
                          <span className="text-xs text-[#A8A8A8]">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          {warehouse.warehouse_type !== 'RETURN' && !warehouse.is_default_sales && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSetDefault(warehouse.id)}
                            >
                              Make Default Sales
                            </Button>
                          )}
                          {warehouse.warehouse_type === 'RETURN' && !warehouse.is_default_return && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSetDefaultReturn(warehouse.id)}
                            >
                              Make Default Return
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditWarehouse(warehouse)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-[#2D2420]">Warehouse Stock</h2>
              <p className="text-sm text-[#A8A8A8]">Adjust inventory or transfer between warehouses</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => { resetAdjustForm(); setShowAdjustModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Adjust Stock
              </Button>
              <Button variant="outline" onClick={() => { resetTransferForm(); setShowTransferModal(true); }}>
                <ArrowLeftRight className="w-4 h-4 mr-2" />
                Transfer Stock
              </Button>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-4">
            <div className="w-full lg:w-72">
              <Label>Warehouse</Label>
              <Select value={selectedWarehouseId} onValueChange={setSelectedWarehouseId}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {stockLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : stock.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-[#A8A8A8]">
              <WarehouseIcon className="w-6 h-6 mr-2" />
              No stock records yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Product</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">SKU</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">On Hand</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Available</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {stock.map((item) => (
                    <tr key={`${item.variant_id}`} className="hover:bg-[#FAFAF9]/60">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-[#2D2420]">{item.master_name}</p>
                          <p className="text-sm text-[#A8A8A8]">{item.pack_size_label}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-[#A8A8A8]">{item.sku}</td>
                      <td className={cn("py-3 px-4 text-right font-semibold", item.is_negative ? "text-[#E76F51]" : "text-[#2D2420]")}>{item.on_hand}</td>
                      <td className="py-3 px-4 text-right text-[#2D2420]">{item.available}</td>
                      <td className="py-3 px-4 text-right">
                        {item.is_negative ? (
                          <Badge className="bg-red-100 text-red-800 text-xs inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Negative
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800 text-xs">OK</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value="raw" className="space-y-6">
          <Card className="bg-white border-[#E5E5E5]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-[#2D2420]">Raw Items</h2>
                  <p className="text-sm text-[#A8A8A8]">Manage raw material names</p>
                </div>
                <Button onClick={() => { resetRawItemForm(); setShowRawItemModal(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Raw Item
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
                </div>
              ) : rawItems.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-[#A8A8A8]">
                  <WarehouseIcon className="w-6 h-6 mr-2" />
                  No raw items yet
                </div>
              ) : (
                <div className="overflow-x-auto mt-4">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {rawItems.map((item) => (
                        <tr key={item.id} className="hover:bg-[#FAFAF9]/60">
                          <td className="py-3 px-4 text-[#2D2420] font-medium">{item.name}</td>
                          <td className="py-3 px-4">
                            <Badge className={cn("text-xs", item.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800")}>
                              {item.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="icon" onClick={() => handleEditRawItem(item)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E5E5E5]">
            <CardContent className="p-4 space-y-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-[#2D2420]">Raw Stock</h2>
                  <p className="text-sm text-[#A8A8A8]">Receive raw bags and move to packing (FIFO)</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="outline" onClick={() => { resetRawReceiveForm(); setShowRawReceiveModal(true); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Receive Raw Stock
                  </Button>
                  <Button variant="outline" onClick={() => { resetRawTransferForm(); setShowRawTransferModal(true); }}>
                    <ArrowLeftRight className="w-4 h-4 mr-2" />
                    Move to Packing
                  </Button>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row gap-4">
                <div className="w-full lg:w-72">
                  <Label>Warehouse</Label>
                  <Select value={selectedRawWarehouseId} onValueChange={setSelectedRawWarehouseId}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select warehouse" />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.map((w) => (
                        <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {rawStockLoading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
                </div>
              ) : rawLots.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-[#A8A8A8]">
                  <WarehouseIcon className="w-6 h-6 mr-2" />
                  No raw stock lots yet
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Stock Ref</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Raw Item</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Bags</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Total KG</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">KG/Bag</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Received</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {rawLots.map((lot) => (
                        <tr key={lot.id} className="hover:bg-[#FAFAF9]/60">
                          <td className="py-3 px-4 text-[#2D2420] font-medium">{lot.stock_ref_no}</td>
                          <td className="py-3 px-4 text-[#2D2420]">{lot.raw_item_name}</td>
                          <td className="py-3 px-4 text-right text-[#2D2420]">{lot.bags_count}</td>
                          <td className="py-3 px-4 text-right text-[#2D2420]">{lot.kg_total}</td>
                          <td className="py-3 px-4 text-right text-[#A8A8A8]">{lot.kg_per_bag?.toFixed(2)}</td>
                          <td className="py-3 px-4 text-[#A8A8A8]">{formatDateTime(lot.received_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E5E5E5]">
            <CardContent className="p-4 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-[#2D2420]">Raw Stock Movement History</h2>
                <p className="text-sm text-[#A8A8A8]">Track receipts and transfers</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={rawMovementType} onValueChange={setRawMovementType}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="receive">Receive</SelectItem>
                      <SelectItem value="transfer_out">Transfer Out</SelectItem>
                      <SelectItem value="transfer_in">Transfer In</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Raw Item</Label>
                  <Select value={rawMovementItem} onValueChange={setRawMovementItem}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {rawItemOptions.map((item) => (
                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date From</Label>
                  <Input type="date" value={rawMovementDateFrom} onChange={(e) => setRawMovementDateFrom(e.target.value)} className="border-[#E5E5E5]" />
                </div>
                <div>
                  <Label>Date To</Label>
                  <Input type="date" value={rawMovementDateTo} onChange={(e) => setRawMovementDateTo(e.target.value)} className="border-[#E5E5E5]" />
                </div>
              </div>

              {rawMovementLoading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
                </div>
              ) : rawMovements.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-[#A8A8A8]">
                  <WarehouseIcon className="w-6 h-6 mr-2" />
                  No movements recorded
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Warehouse</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Raw Item</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Bags</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">KG</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Stock Ref</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Note</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {rawMovements.map((movement) => (
                        <tr key={movement.id} className="hover:bg-[#FAFAF9]/60">
                          <td className="py-3 px-4 text-[#2D2420]">{formatDateTime(movement.created_at)}</td>
                          <td className="py-3 px-4 text-[#2D2420]">{movement.warehouse_name}</td>
                          <td className="py-3 px-4 text-[#2D2420]">{movement.raw_item_name}</td>
                          <td className="py-3 px-4 text-[#A8A8A8]">{movement.type}</td>
                          <td className={cn("py-3 px-4 text-right font-semibold", movement.bags < 0 ? "text-[#E76F51]" : "text-[#2D2420]")}>{movement.bags}</td>
                          <td className={cn("py-3 px-4 text-right", movement.kg < 0 ? "text-[#E76F51]" : "text-[#2D2420]")}>{movement.kg}</td>
                          <td className="py-3 px-4 text-[#A8A8A8]">{movement.stock_ref_no || '-'}</td>
                          <td className="py-3 px-4 text-[#A8A8A8]">{movement.note || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="packing" className="space-y-6">
          <Card className="bg-white border-[#E5E5E5]">
            <CardContent className="p-4 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-[#2D2420]">Packing Requests</h2>
                <p className="text-sm text-[#A8A8A8]">Track orders that need packing due to stock shortages</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <Label>Status</Label>
                  <Select value={packingStatus} onValueChange={setPackingStatus}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Search</Label>
                  <Input
                    value={packingSearch}
                    onChange={(e) => setPackingSearch(e.target.value)}
                    placeholder="Order ref or customer"
                    className="border-[#E5E5E5]"
                  />
                </div>
                <div>
                  <Label>Date From</Label>
                  <Input
                    type="date"
                    value={packingDateFrom}
                    onChange={(e) => setPackingDateFrom(e.target.value)}
                    className="border-[#E5E5E5]"
                  />
                </div>
                <div>
                  <Label>Date To</Label>
                  <Input
                    type="date"
                    value={packingDateTo}
                    onChange={(e) => setPackingDateTo(e.target.value)}
                    className="border-[#E5E5E5]"
                  />
                </div>
              </div>

              {packingLoading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
                </div>
              ) : packingRequests.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-[#A8A8A8]">
                  <AlertTriangle className="w-6 h-6 mr-2" />
                  No packing requests
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Order</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Customer</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Shortage Items</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Expected Delivery</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {packingRequests.map((request) => (
                        <tr key={request.id} className="hover:bg-[#FAFAF9]/60 align-top">
                          <td className="py-3 px-4 text-[#2D2420] whitespace-nowrap">
                            {formatDateTime(request.created_at)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-[#2D2420] font-medium">{request.order_ref || '-'}</div>
                            <div className="text-sm text-[#A8A8A8]">{request.order_id || ''}</div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-[#2D2420]">{request.customer_name || '-'}</div>
                            <div className="text-sm text-[#A8A8A8]">{request.customer_shop || ''}</div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="space-y-2">
                              {(request.items || []).map((item, idx) => (
                                <div key={`${request.id}-item-${idx}`} className="text-sm">
                                  <div className="text-[#2D2420] font-medium">
                                    {item.product_name} {item.pack_size_label ? `(${item.pack_size_label})` : ''}
                                  </div>
                                  <div className="text-[#A8A8A8]">
                                    Required: {Math.round(item.required_qty || 0)} • Available: {Math.round(item.available_qty || 0)} • Short: {Math.round(item.shortfall || 0)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-[#2D2420] whitespace-nowrap">
                            {request.expected_delivery_date ? formatDateTime(request.expected_delivery_date) : '-'}
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={cn("text-xs", request.status === 'completed' ? "bg-green-100 text-green-800" : "bg-[#E76F51]/10 text-[#E76F51]")}>{request.status === 'completed' ? 'Completed' : 'Pending'}</Badge>
                            {request.status === 'completed' && request.remarks ? (
                              <div className="text-xs text-[#A8A8A8] mt-1">{request.remarks}</div>
                            ) : null}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {request.status !== 'completed' ? (
                              <Button variant="outline" size="sm" onClick={() => handleOpenPackingComplete(request)}>
                                Complete
                              </Button>
                            ) : (
                              <span className="text-xs text-[#A8A8A8]">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showPackingCompleteModal} onOpenChange={setShowPackingCompleteModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Complete Packing Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Remarks</Label>
              <Textarea
                value={packingRemarks}
                onChange={(e) => setPackingRemarks(e.target.value)}
                className="border-[#E5E5E5]"
                placeholder="Add completion remarks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPackingCompleteModal(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCompletePackingRequest} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warehouse Modal */}
      <Dialog open={showWarehouseModal} onOpenChange={setShowWarehouseModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">{selectedWarehouse ? 'Edit Warehouse' : 'Add Warehouse'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={warehouseForm.name}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, name: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
            <div>
              <Label>Code</Label>
              <Input
                value={warehouseForm.code}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, code: e.target.value }))}
                className="border-[#E5E5E5]"
                placeholder="Optional"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Textarea
                value={warehouseForm.address}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, address: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
            <div>
              <Label>Warehouse Type</Label>
              <Select
                value={warehouseForm.warehouse_type}
                onValueChange={(value) =>
                  setWarehouseForm((prev) => ({
                    ...prev,
                    warehouse_type: value,
                    tag: value === 'RETURN' ? 'RETURN' : (prev.tag === 'RETURN' ? 'SALES' : prev.tag),
                    is_default_sales: value === 'RETURN' ? false : prev.is_default_sales,
                    is_default_return: value === 'RETURN' ? prev.is_default_return : false
                  }))
                }
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SALES">Sales</SelectItem>
                  <SelectItem value="RETURN">Return</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tag</Label>
              <Select
                value={warehouseForm.tag}
                onValueChange={(value) => setWarehouseForm((prev) => ({ ...prev, tag: value }))}
                disabled={warehouseForm.warehouse_type === 'RETURN'}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select tag" />
                </SelectTrigger>
                <SelectContent>
                  {warehouseForm.warehouse_type === 'RETURN' ? (
                    <SelectItem value="RETURN">RETURN</SelectItem>
                  ) : (
                    <>
                      <SelectItem value="MAIN">MAIN</SelectItem>
                      <SelectItem value="SALES">SALES</SelectItem>
                      <SelectItem value="PACKING">PACKING</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={warehouseForm.is_active}
                onCheckedChange={(value) => setWarehouseForm((prev) => ({ ...prev, is_active: value }))}
              />
            </div>
            {warehouseForm.warehouse_type === 'RETURN' ? (
              <div className="flex items-center justify-between">
                <Label>Default Return Warehouse</Label>
                <Switch
                  checked={warehouseForm.is_default_return}
                  onCheckedChange={(value) => setWarehouseForm((prev) => ({ ...prev, is_default_return: value }))}
                />
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <Label>Default Sales Warehouse</Label>
                <Switch
                  checked={warehouseForm.is_default_sales}
                  onCheckedChange={(value) => setWarehouseForm((prev) => ({ ...prev, is_default_sales: value }))}
                />
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowWarehouseModal(false)}>Cancel</Button>
            <Button onClick={handleSaveWarehouse} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Modal */}
      <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Adjust Stock</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Variant</Label>
              <Select value={adjustForm.variant_id} onValueChange={(value) => setAdjustForm((prev) => ({ ...prev, variant_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select variant" />
                </SelectTrigger>
                <SelectContent>
                  {variantOptions.map((variant) => (
                    <SelectItem key={variant.id} value={variant.id}>
                      {variant.master_name} - {variant.pack_size_label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity (+/-)</Label>
              <Input
                type="number"
                value={adjustForm.qty}
                onChange={(e) => setAdjustForm((prev) => ({ ...prev, qty: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
            <div>
              <Label>Batch Number</Label>
              <Input
                value={adjustForm.batch_no}
                onChange={(e) => setAdjustForm((prev) => ({ ...prev, batch_no: e.target.value }))}
                className="border-[#E5E5E5]"
                placeholder="Optional"
              />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                value={adjustForm.note}
                onChange={(e) => setAdjustForm((prev) => ({ ...prev, note: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAdjustModal(false)}>Cancel</Button>
            <Button onClick={handleAdjustStock} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Adjustment Modal */}
      <Dialog open={showEditAdjustModal} onOpenChange={setShowEditAdjustModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Edit Stock Adjustment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Quantity (+/-)</Label>
              <Input
                type="number"
                value={editAdjustForm.qty}
                onChange={(e) => setEditAdjustForm((prev) => ({ ...prev, qty: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
            <div>
              <Label>Batch Number</Label>
              <Input
                value={editAdjustForm.batch_no}
                onChange={(e) => setEditAdjustForm((prev) => ({ ...prev, batch_no: e.target.value }))}
                className="border-[#E5E5E5]"
                placeholder="Optional"
              />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                value={editAdjustForm.note}
                onChange={(e) => setEditAdjustForm((prev) => ({ ...prev, note: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEditAdjustModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateAdjustment} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Stock Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Transfer Stock</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>From Warehouse</Label>
              <Select value={transferForm.from_warehouse_id} onValueChange={(value) => setTransferForm((prev) => ({ ...prev, from_warehouse_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>To Warehouse</Label>
              <Select value={transferForm.to_warehouse_id} onValueChange={(value) => setTransferForm((prev) => ({ ...prev, to_warehouse_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select destination" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Variant</Label>
              <Select value={transferForm.variant_id} onValueChange={(value) => setTransferForm((prev) => ({ ...prev, variant_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select variant" />
                </SelectTrigger>
                <SelectContent>
                  {variantOptions.map((variant) => (
                    <SelectItem key={variant.id} value={variant.id}>
                      {variant.master_name} - {variant.pack_size_label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity</Label>
              <Input
                type="number"
                value={transferForm.qty}
                onChange={(e) => setTransferForm((prev) => ({ ...prev, qty: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
            <div>
              <Label>Batch Number</Label>
              <Input
                value={transferForm.batch_no}
                onChange={(e) => setTransferForm((prev) => ({ ...prev, batch_no: e.target.value }))}
                className="border-[#E5E5E5]"
                placeholder="Optional"
              />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                value={transferForm.note}
                onChange={(e) => setTransferForm((prev) => ({ ...prev, note: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowTransferModal(false)}>Cancel</Button>
            <Button onClick={handleTransferStock} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raw Item Modal */}
      <Dialog open={showRawItemModal} onOpenChange={setShowRawItemModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">{selectedRawItem ? 'Edit Raw Item' : 'Add Raw Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={rawItemForm.name}
                onChange={(e) => setRawItemForm((prev) => ({ ...prev, name: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={rawItemForm.is_active}
                onCheckedChange={(value) => setRawItemForm((prev) => ({ ...prev, is_active: value }))}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowRawItemModal(false)}>Cancel</Button>
            <Button onClick={handleSaveRawItem} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Raw Stock Modal */}
      <Dialog open={showRawReceiveModal} onOpenChange={setShowRawReceiveModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Receive Raw Stock</DialogTitle>
          </DialogHeader>
          {(() => {
            const bags = parseFloat(rawReceiveForm.bags_count || '0');
            const kgPerBag = parseFloat(rawReceiveForm.kg_per_bag || '0');
            const totalKg = Number.isFinite(bags * kgPerBag) ? bags * kgPerBag : 0;
            return (
              <div className="space-y-4">
                <div>
                  <Label>Warehouse</Label>
                  <Select value={rawReceiveForm.warehouse_id} onValueChange={(value) => setRawReceiveForm((prev) => ({ ...prev, warehouse_id: value }))}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select warehouse" />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.map((w) => (
                        <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Raw Item</Label>
                  <Select value={rawReceiveForm.raw_item_id} onValueChange={(value) => setRawReceiveForm((prev) => ({ ...prev, raw_item_id: value }))}>
                    <SelectTrigger className="border-[#E5E5E5]">
                      <SelectValue placeholder="Select raw item" />
                    </SelectTrigger>
                    <SelectContent>
                      {rawItemOptions.map((item) => (
                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Stock Ref No</Label>
                  <Input value={rawStockRefPreview || 'Generating...'} disabled className="border-[#E5E5E5]" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Bags</Label>
                    <Input
                      type="number"
                      value={rawReceiveForm.bags_count}
                      onChange={(e) => setRawReceiveForm((prev) => ({ ...prev, bags_count: e.target.value }))}
                      className="border-[#E5E5E5]"
                    />
                  </div>
                  <div>
                    <Label>KG per Bag</Label>
                    <Input
                      type="number"
                      value={rawReceiveForm.kg_per_bag}
                      onChange={(e) => setRawReceiveForm((prev) => ({ ...prev, kg_per_bag: e.target.value }))}
                      className="border-[#E5E5E5]"
                    />
                  </div>
                </div>
                <div>
                  <Label>Total KG</Label>
                  <Input value={totalKg ? totalKg.toFixed(2) : '0'} disabled className="border-[#E5E5E5]" />
                </div>
                <div>
                  <Label>Received Date</Label>
                  <Input
                    type="date"
                    value={rawReceiveForm.received_at}
                    onChange={(e) => setRawReceiveForm((prev) => ({ ...prev, received_at: e.target.value }))}
                    className="border-[#E5E5E5]"
                  />
                </div>
                <div>
                  <Label>Note</Label>
                  <Textarea
                    value={rawReceiveForm.note}
                    onChange={(e) => setRawReceiveForm((prev) => ({ ...prev, note: e.target.value }))}
                    className="border-[#E5E5E5]"
                  />
                </div>
              </div>
            );
          })()}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowRawReceiveModal(false)}>Cancel</Button>
            <Button onClick={handleReceiveRawStock} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Receive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Raw Stock Modal */}
      <Dialog open={showRawTransferModal} onOpenChange={setShowRawTransferModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Move to Packing</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>From Warehouse</Label>
              <Select value={rawTransferForm.from_warehouse_id} onValueChange={(value) => setRawTransferForm((prev) => ({ ...prev, from_warehouse_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>To Warehouse (Packing)</Label>
              <Select value={rawTransferForm.to_warehouse_id} onValueChange={(value) => setRawTransferForm((prev) => ({ ...prev, to_warehouse_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select destination" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Raw Item</Label>
              <Select value={rawTransferForm.raw_item_id} onValueChange={(value) => setRawTransferForm((prev) => ({ ...prev, raw_item_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select raw item" />
                </SelectTrigger>
                <SelectContent>
                  {rawItemOptions.map((item) => (
                    <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Required Kg to move</Label>
              <Input
                type="number"
                step="0.001"
                value={rawTransferForm.kg_required}
                onChange={(e) => setRawTransferForm((prev) => ({ ...prev, kg_required: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>

            {rawSuggestions.length > 0 && (
              <div className="rounded-lg border border-[#E5E5E5] p-3 bg-[#FAFAF9]">
                <p className="text-sm font-semibold text-[#2D2420]">Suggested FIFO Bags</p>
                <div className="mt-2 space-y-2">
                  {rawSuggestions.map((s) => (
                    <div key={s.lot_id} className="flex items-center justify-between text-sm text-[#5C4033]">
                      <span>{s.stock_ref_no} • {Number(s.move_bags_exact || s.move_bags || 0).toFixed(3)} bags</span>
                      <span>{s.kg_moved.toFixed(2)} kg</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-sm text-[#2D2420]">
                  Total: {rawSuggestionTotals.total_bags} bags • {rawSuggestionTotals.total_kg.toFixed(2)} kg
                </div>
              </div>
            )}

            <div>
              <Label>Note</Label>
              <Textarea
                value={rawTransferForm.note}
                onChange={(e) => setRawTransferForm((prev) => ({ ...prev, note: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowRawTransferModal(false)}>Cancel</Button>
            <Button onClick={handleTransferRawStock} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Move'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
